import type { TextContentBlock } from '../../utils/content-block-schema'
import type { ContentBlocksAndAdSlotMarkers } from '../types/content-block-types'
import type { NewContentBlock } from '../stores/content-block-store'

const isTextBlock = (block: NewContentBlock<ContentBlocksAndAdSlotMarkers>): block is NewContentBlock<TextContentBlock> => {
  return block.type === 'text' && 'text' in block.data
}

// TODO: Remove when fixed in CMS @see https://bauer-specialist.atlassian.net/browse/TCDW-533
const mergeConsecutiveLists = (elements: string[]): string[] => {
  if (elements.length < 2) {
    return elements
  }

  const mergedElements: string[] = []
  let i = 0

  while (i < elements.length) {
    const currentElement = elements[i]!
    const listMatch = currentElement.match(/^<(ul|ol)>([\s\S]*?)<\/\1>$/)

    if (listMatch) {
      const listType = listMatch[1]!
      let listItems = listMatch[2] || ''
      let j = i + 1

      while (j < elements.length) {
        const nextElement = elements[j]!
        const nextListMatch = nextElement.match(/^<(ul|ol)>([\s\S]*?)<\/\1>$/)

        if (nextListMatch && nextListMatch[1] === listType) {
          listItems += nextListMatch[2] || ''
          j++
        } else {
          break
        }
      }
      mergedElements.push(`<${listType}>${listItems}</${listType}>`)
      i = j
    } else {
      mergedElements.push(currentElement)
      i++
    }
  }
  return mergedElements
}

// TODO: Remove when fixed in CMS @see https://bauer-specialist.atlassian.net/browse/TCDW-483
export function splitTextBlocks (blocks: NewContentBlock<ContentBlocksAndAdSlotMarkers>[]): NewContentBlock<ContentBlocksAndAdSlotMarkers>[] {
  return blocks.reduce<NewContentBlock<ContentBlocksAndAdSlotMarkers>[]>((acc, block) => {
    if (!isTextBlock(block)) {
      acc.push(block)
      return acc
    }

    // Pre-process the text to remove all empty p tags
    const cleanedText = block.data.text.replace(/<p[^>]*>(\s|&nbsp;)*<\/p>/g, '')

    // Split content into top-level elements
    const elements = cleanedText
    // Match complete elements: h2, h3, p, ul, ol with their content
      .match(/<(h[23]|p|ul|ol)[^>]*>[\s\S]*?<\/\1>|(<p[^>]*>(\s|&nbsp;)*<\/p>)/g)
      ?.filter((element) => !element.match(/<p[^>]*>(\s|&nbsp;)*<\/p>/)) || []

    // If there were no meaningful elements to begin with, push the original block and continue.
    // This handles cases like a block with only an image, or text that doesn't split.
    // The filter already removes empty <p> tags, so if elements is empty, it means the block had no content or only empty <p>s.
    if (elements.length === 0) {
      // If original block's text was also effectively empty or just whitespace after filtering,
      // we might not want to push anything or push an empty text block.
      // For now, let's assume the existing tests for empty paragraph removal cover this.
      // If elements is empty, it means there's nothing to make blocks from.
      // If block.data.text was not empty, but elements is, it implies all content was filtered.
      // Consider if we should push the original block or not. The tests will guide this.
      // The 'remove empty paragraph tags' test expects fewer blocks, so not pushing is correct here.
      return acc
    }

    const mergedElements = mergeConsecutiveLists(elements)

    // After merging, if there's only one element, create a single block with that merged content.
    if (mergedElements.length === 1) {
      const newText = mergedElements[0]!.trim()
      // If the original elements array also had only one item, and the text content hasn't changed,
      // then it's essentially the same block, so push the original.
      if (elements.length === 1 && newText === cleanedText.trim()) {
        acc.push(block)
      } else {
        // Otherwise, either a merge happened (elements.length > 1 became mergedElements.length === 1)
        // or the content of a single element was transformed (e.g. by trimming, though mergeConsecutiveLists is the main transformer here).
        // In this case, we push a block with the new/merged text.
        acc.push({
          ...block,
          data: {
            ...block.data,
            paragraphType: 'text',
            text: newText
          },
          // Use a new ID if content changed or was result of merge, otherwise original ID was pushed.
          // The tests for split text blocks explicitly don't check IDs for NEWLY created blocks from a split.
          // For a block that is modified (merged from multiple to one), a new ID is reasonable.
          id: `merged-text-${block.id}`
        })
      }
      return acc
    }

    // If mergedElements is empty after merge (should be caught by elements.length === 0 if merge doesn't add elements)
    // This case should ideally not be reached if elements.length > 0. If mergedElements can be empty, handle like elements.length === 0.
    if (mergedElements.length === 0) {
      return acc
    }

    // Create new blocks for each element if there are multiple elements after merging
    const newBlocks = mergedElements.map((element, index): NewContentBlock<ContentBlocksAndAdSlotMarkers> => ({
      ...block,
      data: {
        paragraphType: 'text',
        text: element.trim()
      },
      // We create a new block so we need to generate a new id
      id: `split-text-${block.id}-${index}`
    }))

    acc.push(...newBlocks)
    return acc
  }, [])
}
